import { nanoid } from '@reduxjs/toolkit';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  CanvasKind,
  CanvasState,
  Connection,
  DiscoveryNode,
  Position,
  SavedLayout,
  Size,
  Widget,
  WidgetConfig,
  WorkspaceState
} from '../types/index.js';
import { tracePipelineBackwards } from '../utils/pipelineTracer.js';
import { widgetRegistry } from '../utils/widgetRegistry.js';

interface CanvasStore extends WorkspaceState {
  // Canvas management
  createCanvas: (name: string, options?: { kind?: CanvasKind; key?: string; makeActive?: boolean }) => string;
  deleteCanvas: (id: string) => void;
  setActiveCanvas: (id: string) => void;
  updateCanvasName: (id: string, name: string) => void;
  setCanvasIdentity: (
    id: string,
    identity: { key?: string; kind?: CanvasKind; name?: string }
  ) => void;
  setCanvasMetadata: (id: string, metadata: Record<string, any>) => void;
  
  // Widget management
  addWidget: (type: string, position: Position, config?: Partial<WidgetConfig>) => string;
  addWidgetToCanvas: (canvasId: string, type: string, position: Position, config?: Partial<WidgetConfig>) => string;
  updateWidget: (canvasId: string, widgetId: string, updates: Partial<Widget>) => void;
  removeWidget: (canvasId: string, widgetId: string) => void;
  clearCanvas: (canvasId: string) => void;
  moveWidget: (canvasId: string, widgetId: string, position: Position) => void;
  resizeWidget: (canvasId: string, widgetId: string, size: Size) => void;
  updateWidgetConfig: (canvasId: string, widgetId: string, config: Partial<WidgetConfig>) => void;
  updateWidgetOutputs: (canvasId: string, widgetId: string, outputs: Partial<Record<string, any>>) => void;
  updateWidgetInputs: (canvasId: string, widgetId: string, inputs: Partial<Record<string, any>>) => void;
  updateWidgetState: (canvasId: string, widgetId: string, state: Partial<Widget['state']>) => void;
  
  // Connection management
  createConnection: (canvasId: string, connection: Omit<Connection, 'id'>) => string;
  removeConnection: (canvasId: string, connectionId: string) => void;
  executeDataFlow: (canvasId: string) => void;
  
  // Discovery Node management
  addDiscoveryNode: (
    canvasId: string,
    node: {
      title: string;
      description: string;
      prompt: string;
      position?: Position;
      anchorWidgetId?: string;
      pinned?: boolean;
    }
  ) => string;
  moveDiscoveryNode: (canvasId: string, nodeId: string, position: Position) => void;
  removeDiscoveryNode: (canvasId: string, nodeId: string) => void;
  clearDiscoveryNodes: (canvasId: string) => void;
  
  // Viewport management
  updateViewport: (canvasId: string, viewport: Partial<CanvasState['viewport']>) => void;
  
  // Grid settings
  updateGridSettings: (canvasId: string, settings: Partial<CanvasState['gridSettings']>) => void;
  
  // Group management
  createGroup: (canvasId: string, widgetIds: string[], name: string, groupId?: string) => string;
  createPipelineGroup: (canvasId: string, targetWidgetId: string, name: string) => string;
  expandGroup: (canvasId: string, groupId: string, groupWidgetId: string) => void;
  updateGroup: (canvasId: string, groupId: string, updates: any) => void;
  zoomToGroup: (canvasId: string, groupId: string) => void;
  zoomOut: (canvasId: string) => void;
  
  // Layout management
  saveLayout: (layout: Omit<SavedLayout, 'id' | 'createdAt' | 'updatedAt'>) => string;
  loadLayout: (layoutId: string) => void;
  deleteLayout: (layoutId: string) => void;
  
  // Utility
  getActiveCanvas: () => CanvasState | undefined;
  getWidget: (canvasId: string, widgetId: string) => Widget | undefined;
  getVisibleWidgets: (canvasId: string) => Widget[];
  getVisibleConnections: (canvasId: string) => Connection[];
}

const createDefaultCanvas = (options: { name?: string; kind?: CanvasKind; key?: string } = {}): CanvasState => {
  const id = nanoid();
  return {
    id,
    name: options.name ?? 'New Canvas',
    key: options.key,
    kind: options.kind ?? 'custom',
    metadata: {},
    widgets: [],
    connections: [],
    groups: [],
    discoveryNodes: [],
    viewport: {
      x: 0,
      y: 0,
      zoom: 1,
    },
    gridSettings: {
      enabled: true,
      size: 20,
    },
  };
};

const defaultCanvas = createDefaultCanvas();

// Memoized selectors for performance optimization
export const selectActiveCanvasId = (state: CanvasStore) => state.activeCanvasId;

export const selectActiveCanvas = (state: CanvasStore) => 
  state.canvases.find(c => c.id === state.activeCanvasId);

export const selectActiveCanvasWidgets = (state: CanvasStore) => {
  const canvas = state.canvases.find(c => c.id === state.activeCanvasId);
  return canvas?.widgets || [];
};

export const selectActiveCanvasConnections = (state: CanvasStore) => {
  const canvas = state.canvases.find(c => c.id === state.activeCanvasId);
  return canvas?.connections || [];
};

export const selectCanvasById = (canvasId: string) => (state: CanvasStore) =>
  state.canvases.find(c => c.id === canvasId);

export const selectWidgetsForCanvas = (canvasId: string) => (state: CanvasStore) => {
  const canvas = state.canvases.find(c => c.id === canvasId);
  return canvas?.widgets || [];
};

export const selectConnectionsForCanvas = (canvasId: string) => (state: CanvasStore) => {
  const canvas = state.canvases.find(c => c.id === canvasId);
  return canvas?.connections || [];
};

// Debounce map to coalesce rapid data flow executions per canvas
const scheduledDataFlowTimers = new Map<string, number>();

export const useCanvasStore = create<CanvasStore>()(
  devtools(
    persist(
      (set, get) => ({
        canvases: [defaultCanvas],
        activeCanvasId: defaultCanvas.id,
        savedLayouts: [],

        createCanvas: (name, options) => {
          const newCanvas = createDefaultCanvas({
            name,
            kind: options?.kind,
            key: options?.key,
          });
          const makeActive = options?.makeActive ?? true;
          set((state) => ({
            canvases: [...state.canvases, newCanvas],
            activeCanvasId: makeActive ? newCanvas.id : state.activeCanvasId,
          }));
          return newCanvas.id;
        },

        deleteCanvas: (id) => {
          set((state) => {
            const filteredCanvases = state.canvases.filter((c) => c.id !== id);
            const newActiveId = state.activeCanvasId === id 
              ? filteredCanvases[0]?.id || ''
              : state.activeCanvasId;
            
            return {
              canvases: filteredCanvases,
              activeCanvasId: newActiveId,
            };
          });
        },

        setActiveCanvas: (id) => {
          set({ activeCanvasId: id });
        },

        updateCanvasName: (id, name) => {
          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === id ? { ...canvas, name } : canvas
            ),
          }));
        },

        setCanvasIdentity: (id, identity) => {
          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === id
                ? {
                    ...canvas,
                    key: identity.key ?? canvas.key,
                    kind: identity.kind ?? canvas.kind,
                    name: identity.name ?? canvas.name,
                  }
                : canvas
            ),
          }));
        },

        setCanvasMetadata: (id, metadata) => {
          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === id
                ? {
                    ...canvas,
                    metadata: { ...(canvas.metadata || {}), ...metadata },
                  }
                : canvas
            ),
          }));
        },

        addWidget: (type, position, config = {}) => {
          const widgetId = nanoid();
          
          // Get widget definition from registry
          const widgetDef = widgetRegistry.get(type);
          
          const newWidget: Widget = {
            id: widgetId,
            type,
            position,
            size: widgetDef?.defaultSize || { width: 300, height: 200 },
            zIndex: Date.now(), // Simple z-index based on creation time
            config: {
              ...widgetDef?.defaultConfig,
              ...config,
            },
            inputs: widgetDef?.inputDefinitions.map(def => ({
              id: def.id,
              name: def.name,
              type: def.type,
              schema: def.schema,
              acceptsMultiple: def.acceptsMultiple,
              value: def.defaultValue,
            })) || [],
            outputs: widgetDef?.outputDefinitions.map(def => ({
              id: def.id,
              name: def.name,
              type: def.type,
              schema: def.schema,
              value: null,
            })) || [],
            state: {
              loading: false,
            },
          };

          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === state.activeCanvasId
                ? { ...canvas, widgets: [...canvas.widgets, newWidget] }
                : canvas
            ),
          }));

          return widgetId;
        },

        addWidgetToCanvas: (canvasId, type, position, config = {}) => {
          const widgetId = nanoid();

          const widgetDef = widgetRegistry.get(type);
          const newWidget: Widget = {
            id: widgetId,
            type,
            position,
            size: widgetDef?.defaultSize || { width: 300, height: 200 },
            zIndex: Date.now(),
            config: {
              ...widgetDef?.defaultConfig,
              ...config,
            },
            inputs: widgetDef?.inputDefinitions.map(def => ({
              id: def.id,
              name: def.name,
              type: def.type,
              schema: def.schema,
              acceptsMultiple: def.acceptsMultiple,
              value: def.defaultValue,
            })) || [],
            outputs: widgetDef?.outputDefinitions.map(def => ({
              id: def.id,
              name: def.name,
              type: def.type,
              schema: def.schema,
              value: null,
            })) || [],
            state: { loading: false },
          };

          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === canvasId
                ? { ...canvas, widgets: [...canvas.widgets, newWidget] }
                : canvas
            ),
          }));

          return widgetId;
        },

        updateWidget: (canvasId, widgetId, updates) => {
          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === canvasId
                ? {
                    ...canvas,
                    widgets: canvas.widgets.map((widget) =>
                      widget.id === widgetId ? { ...widget, ...updates } : widget
                    ),
                    groups: canvas.groups?.map(g => ({
                      ...g,
                      widgets: g.widgets.map(w =>
                        w.id === widgetId ? { ...w, ...updates } : w
                      )
                    })),
                  }
                : canvas
            ),
          }));
        },

        removeWidget: (canvasId, widgetId) => {
          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === canvasId
                ? {
                    ...canvas,
                    widgets: canvas.widgets.filter((w) => w.id !== widgetId),
                    connections: canvas.connections.filter(
                      (c) => c.source.widgetId !== widgetId && c.target.widgetId !== widgetId
                    ),
                  }
                : canvas
            ),
          }));
        },

        clearCanvas: (canvasId) => {
          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === canvasId
                ? {
                    ...canvas,
                    widgets: [],
                    connections: [],
                    groups: [],
                    discoveryNodes: [],
                  }
                : canvas
            ),
          }));
        },

        moveWidget: (canvasId, widgetId, position) => {
          const canvas = get().canvases.find(c => c.id === canvasId);
          if (!canvas) return;
          
          // If we're zoomed into a group, update the widget position in the group
          if (canvas.zoomedGroupId) {
            set((state) => ({
              canvases: state.canvases.map((c) =>
                c.id === canvasId
                  ? {
                      ...c,
                      groups: c.groups?.map(g =>
                        g.id === canvas.zoomedGroupId
                          ? {
                              ...g,
                              widgets: g.widgets.map(w =>
                                w.id === widgetId
                                  ? { ...w, position }
                                  : w
                              )
                            }
                          : g
                      ) || []
                    }
                  : c
              ),
            }));
          } else {
            // Normal widget move on main canvas
            get().updateWidget(canvasId, widgetId, { position });
          }
        },

        resizeWidget: (canvasId, widgetId, size) => {
          get().updateWidget(canvasId, widgetId, { size });
        },

        updateWidgetConfig: (canvasId, widgetId, config) => {
          const widget = get().getWidget(canvasId, widgetId);
          if (widget) {
            get().updateWidget(canvasId, widgetId, {
              config: { ...widget.config, ...config },
            });
          }
        },

        updateWidgetOutputs: (canvasId, widgetId, outputs) => {
          const widget = get().getWidget(canvasId, widgetId);
          if (!widget) return;
          // Compute new outputs and check if any value actually changes
          let changed = false;
          const updatedOutputs = widget.outputs.map(output => {
            const newVal = outputs[output.id] !== undefined ? outputs[output.id] : output.value;
            if (!changed && newVal !== output.value) changed = true;
            return { ...output, value: newVal };
          });
          if (!changed) return;
          get().updateWidget(canvasId, widgetId, { outputs: updatedOutputs });
          // Trigger data flow to connected widgets
          get().executeDataFlow(canvasId);
        },

        updateWidgetInputs: (canvasId, widgetId, inputs) => {
          const widget = get().getWidget(canvasId, widgetId);
          if (!widget) return;
          let changed = false;
          const updatedInputs = widget.inputs.map(input => {
            const newVal = inputs[input.id] !== undefined ? inputs[input.id] : input.value;
            if (!changed && newVal !== input.value) changed = true;
            return { ...input, value: newVal };
          });
          if (!changed) return;
          get().updateWidget(canvasId, widgetId, { inputs: updatedInputs });
        },

        updateWidgetState: (canvasId, widgetId, state) => {
          const widget = get().getWidget(canvasId, widgetId);
          if (!widget) return;
          // Skip update if values are identical by reference/primitive equality
          let changed = false;
          for (const k of Object.keys(state) as Array<keyof Widget['state']>) {
            if (widget.state[k] !== (state as any)[k]) { changed = true; break; }
          }
          if (!changed) return;
          get().updateWidget(canvasId, widgetId, {
            state: { ...widget.state, ...state },
          });
        },

        createConnection: (canvasId, connection) => {
          const canvas = get().canvases.find(c => c.id === canvasId);
          if (!canvas) return '';
          
          const connectionId = nanoid();
          const newConnection: Connection = {
            ...connection,
            id: connectionId,
          };

          // If zoomed into a group, add connection to the group
          if (canvas.zoomedGroupId) {
            set((state) => ({
              canvases: state.canvases.map((c) =>
                c.id === canvasId
                  ? {
                      ...c,
                      groups: c.groups?.map(g =>
                        g.id === canvas.zoomedGroupId
                          ? {
                              ...g,
                              connections: [...g.connections, newConnection]
                            }
                          : g
                      ) || []
                    }
                  : c
              ),
            }));
          } else {
            // Normal connection on main canvas
            set((state) => ({
              canvases: state.canvases.map((canvas) =>
                canvas.id === canvasId
                  ? { ...canvas, connections: [...canvas.connections, newConnection] }
                  : canvas
              ),
            }));
          }

          // Execute data flow to update connected widgets
          get().executeDataFlow(canvasId);

          return connectionId;
        },

        removeConnection: (canvasId, connectionId) => {
          const canvas = get().canvases.find(c => c.id === canvasId);
          if (!canvas) return;
          
          // If zoomed into a group, remove connection from the group
          if (canvas.zoomedGroupId) {
            set((state) => ({
              canvases: state.canvases.map((c) =>
                c.id === canvasId
                  ? {
                      ...c,
                      groups: c.groups?.map(g =>
                        g.id === canvas.zoomedGroupId
                          ? {
                              ...g,
                              connections: g.connections.filter((conn) => conn.id !== connectionId)
                            }
                          : g
                      ) || []
                    }
                  : c
              ),
            }));
          } else {
            // Normal connection removal on main canvas
            set((state) => ({
              canvases: state.canvases.map((canvas) =>
                canvas.id === canvasId
                  ? {
                      ...canvas,
                      connections: canvas.connections.filter((c) => c.id !== connectionId),
                    }
                  : canvas
              ),
            }));
          }
        },

        // Discovery Node management
        addDiscoveryNode: (canvasId, node) => {
          const nodeId = nanoid();

          // Compute position if not provided: prefer anchor-aware radial placement,
          // otherwise place in a right-hand suggestion lane next to widgets.
          const computePosition = (): Position => {
            const canvas = get().canvases.find(c => c.id === canvasId);
            if (!canvas) return { x: 100, y: 100 };

            // 1) Anchor-aware: place docked to the RIGHT of the anchor widget in a tidy grid
            if (node.anchorWidgetId) {
              const anchorWidget = canvas.widgets.find(w => w.id === node.anchorWidgetId);
              if (anchorWidget) {
                const anchoredNodes = (canvas.discoveryNodes || []).filter(n => n.anchorWidgetId === node.anchorWidgetId);
                const index = anchoredNodes.length; // place after existing ones

                // Layout parameters (approximate DiscoveryNode size)
                const cardWidth = 200;
                const cardHeight = 130;
                const gap = 16;
                const margin = 24;
                const maxPerColumn = 4; // stack up to 4 vertically, then start a new column
                const col = Math.floor(index / maxPerColumn);
                const row = index % maxPerColumn;

                const rightEdge = anchorWidget.position.x + anchorWidget.size.width;
                const centerY = anchorWidget.position.y + anchorWidget.size.height / 2;

                // Center the vertical stack around anchor center Y
                const totalHeight = maxPerColumn * cardHeight + (maxPerColumn - 1) * gap;
                const startY = centerY - totalHeight / 2;

                const x = rightEdge + margin + col * (cardWidth + gap);
                const y = startY + row * (cardHeight + gap);
                return { x: Math.round(x), y: Math.round(y) };
              }
            }

            // 2) Right-lane grid next to the rightmost widget
            const rightmost = canvas.widgets.reduce(
              (acc, w) => ({
                maxX: Math.max(acc.maxX, w.position.x + (w.size?.width || 0)),
                minY: Math.min(acc.minY, w.position.y),
              }),
              { maxX: 0, minY: 0 }
            );

            const existing = canvas.discoveryNodes?.length || 0;
            const col = Math.floor(existing / 6);
            const row = existing % 6;
            const cardWidth = 200; // approx DiscoveryNode width
            const cardHeight = 130; // approx DiscoveryNode height
            const gap = 16;
            const baseX = rightmost.maxX + 80 + col * (cardWidth + gap);
            const baseY = (rightmost.minY || 0) + row * (cardHeight + gap);
            return { x: baseX, y: baseY };
          };

          // De-duplicate: if a node with the same title and anchor exists, return it
          const existingCanvas = get().canvases.find(c => c.id === canvasId);
          if (existingCanvas && Array.isArray(existingCanvas.discoveryNodes)) {
            const dup = existingCanvas.discoveryNodes.find(n => n.title === node.title && n.anchorWidgetId === node.anchorWidgetId);
            if (dup) {
              return dup.id;
            }
          }

          const position = node.position || computePosition();

          const newNode: DiscoveryNode = {
            id: nodeId,
            title: node.title,
            description: node.description,
            prompt: node.prompt,
            position,
            createdAt: new Date(),
            anchorWidgetId: node.anchorWidgetId,
            pinned: node.pinned ?? true,
          };

          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === canvasId
                ? { ...canvas, discoveryNodes: [...(canvas.discoveryNodes || []), newNode] }
                : canvas
            ),
          }));

          return nodeId;
        },
        
        removeDiscoveryNode: (canvasId, nodeId) => {
          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === canvasId
                ? { ...canvas, discoveryNodes: (canvas.discoveryNodes || []).filter(n => n.id !== nodeId) }
                : canvas
            ),
          }));
        },
        
        clearDiscoveryNodes: (canvasId) => {
          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === canvasId
                ? { ...canvas, discoveryNodes: [] }
                : canvas
            ),
          }));
        },

        moveDiscoveryNode: (canvasId, nodeId, position) => {
          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === canvasId
                ? {
                    ...canvas,
                    discoveryNodes: (canvas.discoveryNodes || []).map((n) =>
                      n.id === nodeId ? { ...n, position } : n
                    ),
                  }
                : canvas
            ),
          }));
        },

        updateViewport: (canvasId, viewport) => {
          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === canvasId
                ? { ...canvas, viewport: { ...canvas.viewport, ...viewport } }
                : canvas
            ),
          }));
        },

        updateGridSettings: (canvasId, settings) => {
          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === canvasId
                ? { ...canvas, gridSettings: { ...canvas.gridSettings, ...settings } }
                : canvas
            ),
          }));
        },

        createGroup: (canvasId, widgetIds, name, id) => {
          const canvas = get().canvases.find(c => c.id === canvasId);
          if (!canvas) return '';

          const groupId = id || nanoid();
          
          // Find all widgets to be grouped
          const widgetsToGroup = canvas.widgets.filter(w => widgetIds.includes(w.id));
          
          // Find all connections within the group
          const internalConnections = canvas.connections.filter(conn => 
            widgetIds.includes(conn.source.widgetId) && 
            widgetIds.includes(conn.target.widgetId)
          );
          
          // For now, we'll track exposed inputs/outputs but not use them
          const exposedInputs: any[] = [];
          const exposedOutputs: any[] = [];
          
          // Calculate center position of grouped widgets
          const avgPosition = widgetsToGroup.reduce(
            (acc, widget) => ({
              x: acc.x + widget.position.x / widgetsToGroup.length,
              y: acc.y + widget.position.y / widgetsToGroup.length,
            }),
            { x: 0, y: 0 }
          );
          
          // Create the group widget
          const groupWidget: Widget = {
            id: nanoid(),
            type: 'widgetGroup',
            position: avgPosition,
            size: { width: 400, height: 300 },
            zIndex: Math.max(...widgetsToGroup.map(w => w.zIndex || 0)) + 1,
            config: {
              title: name,
              groupId,
            },
            inputs: [], // For now, no inputs/outputs
            outputs: [],
            state: { loading: false },
          };
          
          // Create the group
          const group = {
            id: groupId,
            name,
            widgets: widgetsToGroup,
            connections: internalConnections,
            exposedInputs,
            exposedOutputs,
          };
          
          // Update canvas
          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === canvasId
                ? {
                    ...canvas,
                    // Remove grouped widgets from main canvas
                    widgets: [...canvas.widgets.filter(w => !widgetIds.includes(w.id)), groupWidget],
                    // Remove internal connections and external connections to grouped widgets
                    connections: canvas.connections.filter(conn => {
                      const isInternal = internalConnections.some(ic => ic.id === conn.id);
                      const connectsToGrouped = widgetIds.includes(conn.source.widgetId) || 
                                              widgetIds.includes(conn.target.widgetId);
                      return !isInternal && !connectsToGrouped;
                    }),
                    // Add the group
                    groups: [...(canvas.groups || []), group],
                  }
                : canvas
            ),
          }));
          
          return groupWidget.id;
        },

        createPipelineGroup: (canvasId, targetWidgetId, name) => {
          const canvas = get().canvases.find(c => c.id === canvasId);
          if (!canvas) return '';
          
          // Trace all widgets that contribute to this target widget
          const pipelineWidgetIds = tracePipelineBackwards(targetWidgetId, canvas);
          
          // Find the output widget (should be the target widget)
          const outputWidget = canvas.widgets.find(w => w.id === targetWidgetId);
          if (!outputWidget) return '';
          
          const groupId = nanoid();
          
          // Find all widgets in the pipeline
          const widgetsToGroup = canvas.widgets.filter(w => pipelineWidgetIds.includes(w.id));
          
          // Find all connections within the pipeline
          const internalConnections = canvas.connections.filter(conn => 
            pipelineWidgetIds.includes(conn.source.widgetId) && 
            pipelineWidgetIds.includes(conn.target.widgetId)
          );
          
          // Calculate center position of grouped widgets
          const avgPosition = widgetsToGroup.reduce(
            (acc, widget) => ({
              x: acc.x + widget.position.x / widgetsToGroup.length,
              y: acc.y + widget.position.y / widgetsToGroup.length,
            }),
            { x: 0, y: 0 }
          );
          
          // Create the group widget with the outputs of the target widget
          const groupWidget: Widget = {
            id: nanoid(),
            type: 'widgetGroup',
            position: avgPosition,
            size: { width: 400, height: 300 },
            zIndex: Math.max(...widgetsToGroup.map(w => w.zIndex || 0)) + 1,
            config: {
              title: name,
              groupId,
            },
            inputs: [], // TODO: Add external inputs
            outputs: outputWidget.outputs.map(output => ({
              id: output.id,
              name: output.name,
              type: output.type,
              schema: output.schema, // Preserve schema for color coding
              value: output.value, // Preserve current output values
            })),
            state: { loading: false },
          };
          
          // Create the group
          const group = {
            id: groupId,
            name,
            widgets: widgetsToGroup,
            connections: internalConnections,
            exposedInputs: [],
            exposedOutputs: outputWidget.outputs.map(output => ({
              widgetId: outputWidget.id,
              outputId: output.id,
              groupOutputId: output.id,
            })),
          };
          
          // Update canvas
          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === canvasId
                ? {
                    ...canvas,
                    // Remove grouped widgets from main canvas
                    widgets: [...canvas.widgets.filter(w => !pipelineWidgetIds.includes(w.id)), groupWidget],
                    // Remove internal connections and external connections to grouped widgets
                    connections: canvas.connections.filter(conn => {
                      const isInternal = internalConnections.some(ic => ic.id === conn.id);
                      const connectsToGrouped = pipelineWidgetIds.includes(conn.source.widgetId) || 
                                              pipelineWidgetIds.includes(conn.target.widgetId);
                      return !isInternal && !connectsToGrouped;
                    }),
                    // Add the group
                    groups: [...(canvas.groups || []), group],
                  }
                : canvas
            ),
          }));
          
          // Execute data flow to update the group outputs
          get().executeDataFlow(canvasId);
          
          return groupWidget.id;
        },

        expandGroup: (canvasId, groupId, groupWidgetId) => {
          const canvas = get().canvases.find(c => c.id === canvasId);
          if (!canvas) return;
          
          const group = canvas.groups?.find(g => g.id === groupId);
          if (!group) return;
          
          const groupWidget = canvas.widgets.find(w => w.id === groupWidgetId);
          if (!groupWidget) return;
          
          // Restore widgets with positions relative to group widget position
          const restoredWidgets = group.widgets.map(w => ({
            ...w,
            position: {
              x: groupWidget.position.x + w.position.x - group.widgets[0].position.x,
              y: groupWidget.position.y + w.position.y - group.widgets[0].position.y,
            },
          }));
          
          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === canvasId
                ? {
                    ...canvas,
                    // Remove group widget and add back individual widgets
                    widgets: [
                      ...canvas.widgets.filter(w => w.id !== groupWidgetId),
                      ...restoredWidgets,
                    ],
                    // Restore internal connections
                    connections: [...canvas.connections, ...group.connections],
                    // Remove the group
                    groups: canvas.groups?.filter(g => g.id !== groupId) || [],
                  }
                : canvas
            ),
          }));
        },

        updateGroup: (canvasId, groupId, updates) => {
          set((state) => ({
            canvases: state.canvases.map((canvas) =>
              canvas.id === canvasId
                ? {
                    ...canvas,
                    groups: canvas.groups?.map(g =>
                      g.id === groupId ? { ...g, ...updates } : g
                    ) || [],
                  }
                : canvas
            ),
          }));
        },

        saveLayout: (layout) => {
          const layoutId = nanoid();
          const savedLayout: SavedLayout = {
            ...layout,
            id: layoutId,
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          set((state) => ({
            savedLayouts: [...state.savedLayouts, savedLayout],
          }));

          return layoutId;
        },

        loadLayout: (layoutId) => {
          const layout = get().savedLayouts.find((l) => l.id === layoutId);
          if (layout) {
            set((state) => ({
              canvases: state.canvases.map((canvas) =>
                canvas.id === state.activeCanvasId ? layout.canvasState : canvas
              ),
            }));
          }
        },

        deleteLayout: (layoutId) => {
          set((state) => ({
            savedLayouts: state.savedLayouts.filter((l) => l.id !== layoutId),
          }));
        },

        getActiveCanvas: () => {
          const state = get();
          return state.canvases.find((c) => c.id === state.activeCanvasId);
        },

        getWidget: (canvasId, widgetId) => {
          const canvas = get().canvases.find((c) => c.id === canvasId);
          if (!canvas) return undefined;
          const direct = canvas.widgets.find((w) => w.id === widgetId);
          if (direct) return direct;
          const grouped = canvas.groups?.flatMap(g => g.widgets).find(w => w.id === widgetId);
          return grouped;
        },

        getVisibleWidgets: (canvasId) => {
          const canvas = get().canvases.find((c) => c.id === canvasId);
          if (!canvas) return [];
          
          // If zoomed into a group, return only that group's widgets
          if (canvas.zoomedGroupId) {
            const group = canvas.groups?.find(g => g.id === canvas.zoomedGroupId);
            return group?.widgets || [];
          }
          
          // Otherwise return all widgets on the canvas
          return canvas.widgets;
        },

        getVisibleConnections: (canvasId) => {
          const canvas = get().canvases.find((c) => c.id === canvasId);
          if (!canvas) return [];
          
          // If zoomed into a group, return only that group's connections
          if (canvas.zoomedGroupId) {
            const group = canvas.groups?.find(g => g.id === canvas.zoomedGroupId);
            return group?.connections || [];
          }
          
          // Otherwise return all connections on the canvas
          return canvas.connections;
        },

        zoomToGroup: (canvasId, groupId) => {
          const canvas = get().canvases.find(c => c.id === canvasId);
          if (!canvas) return;
          
          const group = canvas.groups?.find(g => g.id === groupId);
          if (!group) return;
          
          // Calculate the bounding box of group widgets for viewport
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          group.widgets.forEach(widget => {
            minX = Math.min(minX, widget.position.x);
            minY = Math.min(minY, widget.position.y);
            maxX = Math.max(maxX, widget.position.x + widget.size.width);
            maxY = Math.max(maxY, widget.position.y + widget.size.height);
          });
          
          // Calculate center and appropriate zoom
          const centerX = (minX + maxX) / 2;
          const centerY = (minY + maxY) / 2;
          
          set((state) => ({
            canvases: state.canvases.map((c) =>
              c.id === canvasId
                ? {
                    ...c,
                    zoomedGroupId: groupId,
                    breadcrumbs: [
                      { id: canvasId, name: c.name, type: 'canvas' },
                      { id: groupId, name: group.name, type: 'group' }
                    ],
                    viewport: {
                      x: -centerX + 400, // Center in viewport (assuming ~800px width)
                      y: -centerY + 300, // Center in viewport (assuming ~600px height)
                      zoom: 1
                    }
                  }
                : c
            ),
          }));
        },

        zoomOut: (canvasId) => {
          set((state) => ({
            canvases: state.canvases.map((c) =>
              c.id === canvasId
                ? {
                    ...c,
                    zoomedGroupId: undefined,
                    breadcrumbs: undefined,
                    viewport: {
                      x: 0,
                      y: 0,
                      zoom: 1
                    }
                  }
                : c
            ),
          }));
        },

        executeDataFlow: (canvasId) => {
          const dataFlowDebug = typeof window !== 'undefined' &&
            !!(window as any).localStorage?.getItem('DEBUG_DATA_FLOW');

          if (typeof window !== 'undefined') {
            // Debounce within a frame to coalesce bursts
            const existing = scheduledDataFlowTimers.get(canvasId);
            if (existing) window.clearTimeout(existing);
            const timer = window.setTimeout(() => {
              scheduledDataFlowTimers.delete(canvasId);
              const canvas = get().canvases.find((c) => c.id === canvasId);
              if (!canvas) return;

              if (dataFlowDebug) {
                // eslint-disable-next-line no-console
                console.debug('[DATAFLOW] Executing data flow for canvas:', canvasId);
              }

              // Process all connections and accumulate input updates per target widget
              const pendingInputs = new Map<string, Record<string, any>>();
              canvas.connections.forEach(connection => {
                const sourceWidget = canvas.widgets.find(w => w.id === connection.source.widgetId);
                const targetWidget = canvas.widgets.find(w => w.id === connection.target.widgetId);

                if (sourceWidget && targetWidget) {
                  // Find the output value from source
                  let sourceOutput = sourceWidget.outputs.find(o => o.id === connection.source.outputId);
                  
                  // Special handling for widget groups
                  if (sourceWidget.type === 'widgetGroup') {
                    const groupConfig = sourceWidget.config as any;
                    const group = canvas.groups?.find(g => g.id === groupConfig.groupId);
                    
                    if (group) {
                      // Find the actual output widget in the group
                      const exposedOutput = group.exposedOutputs.find(
                        eo => eo.groupOutputId === connection.source.outputId
                      );
                      
                      if (exposedOutput) {
                        const outputWidget = group.widgets.find(w => w.id === exposedOutput.widgetId);
                        if (outputWidget) {
                          const actualOutput = outputWidget.outputs.find(o => o.id === exposedOutput.outputId);
                          if (actualOutput) {
                            // Update the group widget's output with the actual value
                            sourceOutput = {
                              ...sourceOutput!,
                              value: actualOutput.value
                            };
                          }
                        }
                      }
                    }
                  }
                  
                  if (dataFlowDebug) {
                    // eslint-disable-next-line no-console
                    console.debug('[DATAFLOW] Connection:', connection.source.widgetId, '->', connection.target.widgetId);
                    // eslint-disable-next-line no-console
                    console.debug('[DATAFLOW] Source output:', sourceOutput);
                  }
                  
                  if (sourceOutput && sourceOutput.value !== undefined) {
                    // Find the target input definition to check for transformer
                    const widgetDef = widgetRegistry.get(targetWidget.type);
                    const inputDef = widgetDef?.inputDefinitions.find(def => def.id === connection.target.inputId);
                    
                    // Apply transformer if available
                    let value = sourceOutput.value;
                    if (inputDef?.transformer) {
                      if (dataFlowDebug) {
                        // eslint-disable-next-line no-console
                        console.debug('[DATAFLOW] Applying transformer to value:', value);
                      }
                      value = inputDef.transformer(value);
                      if (dataFlowDebug) {
                        // eslint-disable-next-line no-console
                        console.debug('[DATAFLOW] Transformed value:', value);
                      }
                    }

                    if (dataFlowDebug) {
                      // eslint-disable-next-line no-console
                      console.debug('[DATAFLOW] Updating widget input:', connection.target.inputId, 'with value:', value);
                    }

                    // Accumulate target widget input updates (single batch per widget)
                    const existing = pendingInputs.get(targetWidget.id) || {};
                    existing[connection.target.inputId] = value;
                    pendingInputs.set(targetWidget.id, existing);
                  }
                }
              });

              // Apply accumulated input updates per target widget
              for (const [targetId, inputs] of pendingInputs.entries()) {
                get().updateWidgetInputs(canvasId, targetId, inputs);
              }
            }, 16);
            scheduledDataFlowTimers.set(canvasId, timer as unknown as number);
            return;
          }

          // Non-browser/testing fallback: execute immediately
          const canvas = get().canvases.find((c) => c.id === canvasId);
          if (!canvas) return;
          const pendingInputs = new Map<string, Record<string, any>>();
          canvas.connections.forEach(connection => {
            const sourceWidget = canvas.widgets.find(w => w.id === connection.source.widgetId);
            const targetWidget = canvas.widgets.find(w => w.id === connection.target.widgetId);
            if (sourceWidget && targetWidget) {
              let sourceOutput = sourceWidget.outputs.find(o => o.id === connection.source.outputId);
              if (sourceWidget.type === 'widgetGroup') {
                const groupConfig = sourceWidget.config as any;
                const group = canvas.groups?.find(g => g.id === groupConfig.groupId);
                if (group) {
                  const exposedOutput = group.exposedOutputs.find(eo => eo.groupOutputId === connection.source.outputId);
                  if (exposedOutput) {
                    const outputWidget = group.widgets.find(w => w.id === exposedOutput.widgetId);
                    if (outputWidget) {
                      const actualOutput = outputWidget.outputs.find(o => o.id === exposedOutput.outputId);
                      if (actualOutput) {
                        sourceOutput = { ...sourceOutput!, value: actualOutput.value };
                      }
                    }
                  }
                }
              }
              if (sourceOutput && sourceOutput.value !== undefined) {
                const widgetDef = widgetRegistry.get(targetWidget.type);
                const inputDef = widgetDef?.inputDefinitions.find(def => def.id === connection.target.inputId);
                let value = sourceOutput.value;
                if (inputDef?.transformer) value = inputDef.transformer(value);
                const existing = pendingInputs.get(targetWidget.id) || {};
                existing[connection.target.inputId] = value;
                pendingInputs.set(targetWidget.id, existing);
              }
            }
          });
          for (const [targetId, inputs] of pendingInputs.entries()) {
            get().updateWidgetInputs(canvasId, targetId, inputs);
          }
        },
      }),
      {
        name: 'canvas-store',
        partialize: (state) => ({
          canvases: state.canvases,
          activeCanvasId: state.activeCanvasId,
          savedLayouts: state.savedLayouts,
        }),
      }
    )
  )
);
